import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { LoadingState } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { useMountEffect } from '@vista-digital/react-tools';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ImagePlaceholder } from './subcomponents/placeholder';
const b = bemClassBuilder('v-image');
// TODO: Support responsive image loading. #286580
export const Image = observer(props => {
    const imgRef = useRef(null);
    const { placeholderType, onError, onLoad, className, fallbackSrc } = props, imageAttributes = __rest(props, ["placeholderType", "onError", "onLoad", "className", "fallbackSrc"]);
    const [src, setSrc] = useState(props.src);
    const [loadingState, setLoadingState] = useState(LoadingState.Loading);
    useEffect(() => setSrc(src), [src, fallbackSrc]);
    useEffect(() => {
        if (!src)
            setLoadingState(LoadingState.NotLoaded);
        else
            setLoadingState(LoadingState.Loading);
    }, [src]);
    if (!props.src && fallbackSrc)
        throw new Error('Must provide an image src before providing a fallback.');
    const validSrc = !!(src === null || src === void 0 ? void 0 : src.length) || !!(fallbackSrc === null || fallbackSrc === void 0 ? void 0 : fallbackSrc.length);
    const handleLoaded = useCallback(() => {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad();
        setLoadingState(LoadingState.Success);
    }, [onLoad]);
    const handleError = () => {
        onError === null || onError === void 0 ? void 0 : onError();
        setLoadingState(LoadingState.Failure);
        if (fallbackSrc && src !== fallbackSrc)
            setSrc(fallbackSrc);
    };
    // Sometimes the image has already loaded when the component mounts, as it has been cached.
    // So we must manually call handleLoaded to hide the placeholder image.
    useMountEffect(() => {
        var _a, _b;
        if ((_a = imgRef.current) === null || _a === void 0 ? void 0 : _a.complete) {
            (_b = imgRef.current) === null || _b === void 0 ? void 0 : _b.decode().then(() => handleLoaded()).catch(() => handleError());
        }
    });
    const showImg = validSrc && loadingState !== LoadingState.Failure;
    const showPlaceholder = !validSrc || loadingState !== LoadingState.Success;
    return (_jsxs("div", Object.assign({ className: b.block({ loadingState }, className) }, { children: [showImg && (_jsx("img", Object.assign({ ref: imgRef }, imageAttributes, { className: b.element('img', { loadingState }), src: src, onLoad: handleLoaded, onError: handleError }))), placeholderType && showPlaceholder && (_jsx(ImagePlaceholder, { type: placeholderType, className: b.element('placeholder', { loadingState }) }))] })));
});
Image.displayName = 'Image';
